import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Rnd } from 'react-rnd';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import DateTime from 'components/DateTime';

import './TimelineDraggable.styles.css';

const handleStyles = {
    top: '5px',
    bottom: '5px',
    height: 'auto',
    width: '6px'
};

const leftHandleStyles = Object.assign({}, handleStyles, {
    left: '-4px'
});

const rightHandleStyles = Object.assign({}, handleStyles, {
    right: '-4px'
});

const ENABLE_RESIZING = {
    left: true,
    right: true
};

const RESIZE_HANDLE_CLASSES = {
    left: 'resize-handle resize-handle-left',
    right: 'resize-handle resize-handle-right'
};

const RESIZE_HANDLE_STYLES = {
    left: leftHandleStyles,
    right: rightHandleStyles
};

const getStart = (start, chartStart) => {
    return moment(start).diff(chartStart, 'minutes');
};

const getDuration = (start, end) => {
    return moment(end).diff(start, 'minutes');
};

const MINUTES_IN_DAY = 1440;

const TimelineDraggable = ({ start, end, chartStartDate, dayWidth, minHeight, tooltipPlacement, overflowing, snapToDay, handleStyles, boundToParent = true, onChange, onResizingChange, onDraggingChange, children }) => {
    const [dragging, setDragging] = useState(false);
    const [resizing, setResizing] = useState(false);
    const [draggingStart, setDraggingStart] = useState(null);
    const [draggingEnd, setDraggingEnd] = useState(null);
    const [dragStartedPos, setDragStartedPos] = useState(null);
    const [dragStartedOn, setDragStartedOn] = useState(null);
    const rndRef = useRef(null);
    const { t } = useTranslation('common');

    const minuteWidth = useMemo(() => dayWidth / MINUTES_IN_DAY, [dayWidth]);

    const computedStart = useMemo(() => {
        return getStart(start, chartStartDate);
    }, [start, chartStartDate]);

    const movingOrStaticStart = useMemo(() => {
        if (dragging || resizing) {
            return getStart(draggingStart || start, chartStartDate);
        }

        return computedStart;
    }, [dragging, resizing, draggingStart, start, chartStartDate, computedStart]);

    const duration = useMemo(() => {
        return getDuration(start, end) + 1;
    }, [start, end]);

    const durationInDays = useMemo(() => {
        return Math.round(duration / MINUTES_IN_DAY);
    }, [duration]);

    const movingOrStaticDuration = useMemo(() => {
        if (dragging || resizing) {
            return getDuration(draggingStart || start, draggingEnd || end) + 1;
        }

        return duration;
    }, [dragging, resizing, draggingStart, draggingEnd, start, end, duration]);

    const width = useMemo(() => movingOrStaticDuration * minuteWidth, [movingOrStaticDuration, minuteWidth]);
    const left = useMemo(() => movingOrStaticStart * minuteWidth, [movingOrStaticStart, minuteWidth]);

    const rndDefault = useMemo(() => ({
        x: left,
        y: 0,
        width
    }), [left, width]);

    const barStyle = useMemo(() => ({
        position: 'absolute',
        left: `${left}px`,
        width: `${width}px`,
        height: '100%'
    }), [left, width]);

    const resizeGrid = useMemo(() => ([snapToDay ? dayWidth : 1, 1]), [snapToDay, dayWidth]);
    const dragGrid = useMemo(() => ([snapToDay ? dayWidth : 1, 1]), [snapToDay, dayWidth]);

    const resizeHandleStyles = useMemo(() => {
        if (handleStyles) {
            return {
                left: Object.assign({}, RESIZE_HANDLE_STYLES.left, handleStyles.left),
                right: Object.assign({}, RESIZE_HANDLE_STYLES.right, handleStyles.right)
            };
        }

        return RESIZE_HANDLE_STYLES;
    }, [handleStyles]);

    const changeStartEnd = useCallback((nextStartM, nextEndM) => {
        const nextStart = (snapToDay ? nextStartM.startOf('day') : nextStartM).valueOf();
        const nextEnd = (snapToDay ? nextEndM.endOf('day') : nextEndM).valueOf();

        if (nextStart === start && nextEnd === end) {
            return;
        }

        // Drop before changing
        setTimeout(() => {
            onChange && onChange(nextStart, nextEnd);
        }, 50);
    }, [start, end, snapToDay, onChange]);

    const getEndFromStart = useCallback(start => {
        if (snapToDay) {
            return start.clone().add(durationInDays - 1, 'days');
        }

        return start.clone().add(duration - 1, 'minutes');
    }, [snapToDay, duration, durationInDays]);

    const getDateByPosition = useCallback(position => {
        const date = moment(chartStartDate).add(position / minuteWidth, 'minutes');

        if(!snapToDay) {
            return date;
        }

        if (date.hours() > 23/2) {
            date.add(1, 'days');
        }

        return date.startOf('day');
    }, [snapToDay, chartStartDate, minuteWidth]);

    const handleResizeStart = () => {
        setResizing(true);
    };

    const handleResizeStop = useCallback((event, direction, element, size, position) => {
        if (direction === 'left') {
            changeStartEnd(getDateByPosition(position.x), moment(end))
        } else if (direction === 'right') {
            changeStartEnd(moment(start), getDateByPosition(position.x - minuteWidth + element.clientWidth));
        }

        setTimeout(() => {
            setResizing(false);
            setDraggingStart(null);
            setDraggingEnd(null);
        }, 60);
    }, [changeStartEnd, getDateByPosition, end, start, minuteWidth, setResizing, setDraggingStart, setDraggingEnd]);

    const handleResize = useCallback((event, direction, element, size, position) => {
        if (direction === 'left') {
            setDraggingStart(getDateByPosition(position.x).valueOf());
            setDraggingEnd(moment(end).valueOf());
        } else if (direction === 'right') {
            setDraggingStart(moment(start).valueOf())
            setDraggingEnd(getDateByPosition(position.x - minuteWidth + element.clientWidth).valueOf());
        }
    }, [setDraggingStart, setDraggingEnd, getDateByPosition, end, start, minuteWidth]);

    const handleDragStart = useCallback(event => {
        setDragStartedPos(event.clientX);
        setDragStartedOn(new Date());
        setDragging(true);
    }, []);

    const handleDragStop = useCallback((event, dragData) => {
        const newStart = getDateByPosition(dragData.x);
        changeStartEnd(newStart, getEndFromStart(newStart));

        setTimeout(() => {
            setDragging(false);
            setDraggingStart(null);
            setDraggingEnd(null);
        }, 60);
    }, [getDateByPosition, changeStartEnd, getEndFromStart]);

    const handleDrag = useCallback((event, dragData) => {
        const newStart = getDateByPosition(dragData.x);

        setDraggingStart(newStart.valueOf());
        setDraggingEnd(getEndFromStart(newStart).valueOf());
    }, [getDateByPosition, getEndFromStart]);

    useEffect(() => {
        if (!(resizing || dragging || !rndRef.current)) {
            rndRef.current.updateSize({ width });
        }
    }, [width, rndRef, resizing, dragging]);

    useEffect(() => {
        if (!(resizing || dragging || !rndRef.current)) {
            rndRef.current.updatePosition({ x: left });
        }

    }, [left, rndRef, resizing, dragging]);

    useEffect(() => {
        onResizingChange && onResizingChange(resizing);
    }, [resizing, onResizingChange]);

    useEffect(() => {
        onDraggingChange && onDraggingChange(dragging, dragStartedPos, dragStartedOn);
    }, [dragging, onDraggingChange]);

    return (
        <div className={'timeline-draggable' + (dragging ? ' dragging' : '') + (resizing ? ' resizing' : '') + (overflowing ? ' overflowing' : '')}>
            <Rnd
                ref={rndRef}
                default={rndDefault}
                dragAxis={'x'}
                enableResizing={ENABLE_RESIZING}
                resizeHandleClasses={RESIZE_HANDLE_CLASSES}
                resizeHandleStyles={resizeHandleStyles}
                minWidth={dayWidth}
                minHeight={minHeight}
                resizeGrid={resizeGrid}
                dragGrid={dragGrid}
                bounds={boundToParent ? 'parent' : undefined}
                onResizeStop={handleResizeStop}
                onResize={handleResize}
                onResizeStart={handleResizeStart}
                onDragStart={handleDragStart}
                onDragStop={handleDragStop}
                onDrag={handleDrag}
            >
                { children }

                {
                    (dragging || resizing) && draggingStart && draggingEnd ? (
                        <div className="timeline-draggable-tooltip overlay-ui-element tooltip visible" data-placement={tooltipPlacement}>
                            <i className="overlay-ui-element-arrow" />

                            <p>
                                <strong>{ t('Start Date') }:&nbsp;</strong>
                                <DateTime format="fullDate">{draggingStart}</DateTime>
                            </p>

                            <p>
                                <strong>{ t('End Date') }:&nbsp;</strong>
                                <DateTime format="fullDate">{draggingEnd}</DateTime>
                            </p>
                        </div>
                    ) : null
                }
            </Rnd>
        </div>
    )
}

TimelineDraggable.propTypes = {
    start: PropTypes.number,
    end: PropTypes.number,
    chartStartDate: PropTypes.number,
    dayWidth: PropTypes.number
};

export default TimelineDraggable;
